import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { SafePipe } from '@nha/pipes/safe.pipe';
import { NHATestDirective } from '@nha/directives/test/test.directive';
import { PortalLinkFacade } from '@nha/components/app-launcher/state/portal-link.facade';
import { RecentLinksService } from '@nha/components/app-launcher/services/recent-links.service';
import { IPortalLink } from '@nha/api/core/types/portal-link.type';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { sanitizeSvgViewBox } from '@nha/functions';

@Component({
	selector: 'nha-portal-link',
	templateUrl: './portal-link.component.html',
	styleUrls: ['./portal-link.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [NHATestDirective, CommonModule, MatIconModule, SafePipe, MatButtonModule, MatTooltipModule]
})
export class NHAPortalLinkComponent {
	portalLinkFacade = inject(PortalLinkFacade);
	private recentLinks = inject(RecentLinksService);

	@Input() portalLinkName: string;
	@Input() portalLinkID: number;
	@Input() linkUrl: string;
	@Input() iconColor: string;
	// Icon SVGs come from admin-entered data; strip a malformed viewBox on the way in so it
	// doesn't spam the console with parse errors (the icon renders the same either way).
	@Input() set iconSVG(value: string) {
		this._iconSVG = sanitizeSvgViewBox(value);
	}
	get iconSVG(): string {
		return this._iconSVG;
	}
	private _iconSVG: string;
	@Input() iconClass: string;
	@Input() iconLetters: string;
	@Input() isFavorite: boolean;
	@Input() hideStar: boolean = false;
	@Output() toggleFavorite = new EventEmitter<boolean>();
	hover: boolean = false;

	// Whole-link convenience input so callers can bind [link]="link" instead of repeating the
	// eight field bindings; individual inputs above stay for call sites that map fields directly.
	@Input() set link(link: IPortalLink) {
		this.portalLinkID = link.portalLinkID;
		this.portalLinkName = link.portalLinkName;
		this.linkUrl = link.linkUrl;
		this.iconColor = link.iconColor;
		this.iconSVG = link.iconSVG;
		this.iconClass = link.iconClass;
		this.iconLetters = link.iconLetters;
		this.isFavorite = link.isFavorite;
	}

	onToggleClick(isFavorite: boolean): void {
		this.isFavorite = isFavorite;
		this.portalLinkFacade.setFavorite(this.portalLinkID, isFavorite);
	}

	// Records the open for the Recent list. Fires alongside the anchor's own navigation
	// (target="_blank"); the recording is synchronous + best-effort so it never blocks it.
	// auxclick covers middle-click "open in new tab"; Enter on the anchor fires click natively.
	onOpen(): void {
		this.recentLinks.record(this.portalLinkID);
	}
}
